import React, { useEffect, useRef, useState } from 'react'
import { Stack, Typography } from '@mui/material'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { BEIGE_300, GRAY_700, RhetorikFontFamily } from 'constants/styling/theme'
import { Path } from 'constants/router'
import { MUIButton } from 'components/common/MUIButton'
import { useUserData } from 'components/contexts/UserDataContext'
import LoadingPage from 'components/pages/General/LoadingPage/LoadingPage'
import { useDrafts } from 'dataQueries/purchaseFlowDrafts.query'
import { useServerSessionLookup } from 'dataQueries/purchaseFlowRevampSessionStatus.query'
import { useAuth0 } from 'utils/auth'
import { logger } from 'utils/logging'

import { usePfStore } from '../store'
import { useResumeDraft } from '../drafts/useResumeDraft'
import { placedSessionOrder } from '../drafts/placedSessions'
import { propertyRefFromDetail } from '../entry/useEmbedPresetProperty'
import { PfStepPath, usePfBasePath, usePfRootPath } from './pfBasePath'
import { usePfEmbed } from './usePfEmbed'
import { PfRevampEventName, dispatchPfRevampEvent } from '../analytics'

type RestoreState = 'checking' | 'ready' | 'ordered' | 'missing' | 'error'

/** The step a resumed draft was saved on, for a session URL that names none. */
const SAVED_STEP_PATH: Partial<Record<string, string>> = {
  [Path.PURCHASE_FLOW_REVAMP_INSTRUCTIONS]: PfStepPath.INSTRUCTIONS,
  [Path.PURCHASE_FLOW_REVAMP_BILLING]: PfStepPath.BILLING,
}

/**
 * Restores the order a session URL names when the page holds none, as after a reload: the session's saved draft on
 * this device is loaded and the step in the URL reopened. Without a draft here, a client's session is looked up on the
 * server in their current workspace, the one their drafts are listed for: an open one reopens on the catalogue for
 * its property (the cart lived only in the draft), an ordered one says so. A session that ended in an order, or is
 * unknown, gets a page saying so with a way to start a new order. A session ordered here whose payment did not go
 * through reopens on the Summary, locked to retrying the payment. A failed lookup offers to try again.
 */
export const PfSessionRestore: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { sessionId = '' } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const base = usePfBasePath()
  const { baseUserData, userPropertyWorkspaceId, isWorkspaceResolved } = useUserData()
  const ownerId = baseUserData?.id
  const { loading: authLoading, roles } = useAuth0()
  const drafts = useDrafts()
  const resume = useResumeDraft()
  const lookUpServerSession = useServerSessionLookup()
  const [serverOrderId, setServerOrderId] = useState<number | null>(null)

  const holdsThisSession = usePfStore((s) => s.draft.sessionId === sessionId || s.items.length > 0)
  const [state, setState] = useState<RestoreState>(holdsThisSession ? 'ready' : 'checking')
  const started = useRef(false)
  // A lookup that answers after the page left this session must not navigate or write the store.
  const liveSessionId = useRef<string | null>(sessionId)
  useEffect(() => {
    liveSessionId.current = sessionId
    return () => { liveSessionId.current = null }
  }, [sessionId])

  useEffect(() => {
    if (state !== 'checking' || started.current || !ownerId || authLoading) return
    if (roles.isClient && !isWorkspaceResolved) return
    // A failed query keeps reporting the error while it refetches, so only its settled answer decides.
    if (drafts.isFetching) return
    if (drafts.isError) {
      logRestore('drafts_unavailable', sessionId, drafts.error)
      setState('error')
      return
    }
    if (!drafts.isSuccess) return
    started.current = true

    // An order placed from this session wins over a draft whose delete failed: its payment cannot be retried here.
    if (placedSessionOrder(sessionId)) {
      logRestore('ordered', sessionId)
      setState('ordered')
      return
    }
    const draft = drafts.data.find((entry) => entry.sessionId === sessionId)
    if (!draft) {
      // An admin has no workspace of their own to ask in; their client's workspace lived only in the draft.
      if (!roles.isClient || !userPropertyWorkspaceId) {
        logRestore(roles.isClient ? 'no_draft_no_workspace' : 'no_draft_not_client', sessionId)
        setState('missing')
        return
      }
      const workspaceId = userPropertyWorkspaceId
      lookUpServerSession(workspaceId, sessionId)
        .then((found) => {
          if (liveSessionId.current !== sessionId) return
          if (found.kind === 'unknown') {
            logRestore(found.reason === 'property' ? 'property_unknown_on_server' : 'session_unknown_on_server', sessionId, undefined, workspaceId)
            setState('missing')
            return
          }
          if (found.kind === 'ordered') {
            logRestore('ordered_on_server', sessionId, undefined, workspaceId)
            dispatchPfRevampEvent({ type: PfRevampEventName.SESSION_RESTORED, payload: { source: 'server', ordered: true } })
            setServerOrderId(found.orderId)
            setState('ordered')
            return
          }
          // Whatever the page held belongs to another order; only the server's session and property carry over.
          const { resetPersistedState } = usePfStore.getState()
          resetPersistedState()
          const { setProperty, setSessionId } = usePfStore.getState()
          setProperty(propertyRefFromDetail(found.property))
          setSessionId(sessionId, workspaceId, found.property.id)
          logRestore('resumed_from_server', sessionId, undefined, workspaceId)
          dispatchPfRevampEvent({ type: PfRevampEventName.SESSION_RESTORED, payload: { source: 'server', ordered: false } })
          setState('ready')
          navigate(`${base}/${PfStepPath.CATALOGUE}`, { replace: true })
        })
        .catch((error: unknown) => {
          if (liveSessionId.current !== sessionId) return
          logRestore('server_lookup_failed', sessionId, error, workspaceId)
          setState('error')
        })
      return
    }

    const step = pathname.slice(base.length)
    resume(draft)
      .then((resumePath) => {
        if (!resumePath) {
          logRestore('resume_refused', sessionId)
          setState('missing')
          return
        }
        logRestore('resumed', sessionId)
        setState('ready')
        const stepPath = resumePath === Path.PURCHASE_FLOW_REVAMP_SELECT_PROPERTY
          ? PfStepPath.SELECT_PROPERTY
          : resumePath === Path.PURCHASE_FLOW_REVAMP_SUMMARY ? PfStepPath.SUMMARY : null
        // A URL without a step opens the step the draft was saved on, like the drawer does (QA-579).
        const savedStepPath = SAVED_STEP_PATH[resumePath] ?? PfStepPath.CATALOGUE
        const target = stepPath
          ? `${base}/${stepPath}`
          : !step || step === '/' ? `${base}/${savedStepPath}` : `${base}${step}`
        navigate(target, { replace: true })
      })
      .catch((error: unknown) => {
        logRestore('resume_failed', sessionId, error)
        setState('missing')
      })
  }, [state, ownerId, authLoading, roles.isClient, isWorkspaceResolved, userPropertyWorkspaceId, drafts.isFetching, drafts.isError, drafts.error, drafts.isSuccess, drafts.data, sessionId, pathname, base, resume, lookUpServerSession, navigate])

  const retry = () => {
    started.current = false
    setState('checking')
    void drafts.refetch()
  }

  if (state === 'checking') return <LoadingPage />
  if (state === 'error') return <PfSessionUnavailable failed onRetry={retry} />
  if (state === 'ordered' || state === 'missing') return <PfSessionUnavailable ordered={state === 'ordered'} orderId={placedSessionOrder(sessionId)?.orderId ?? serverOrderId ?? undefined} />
  return <>{children}</>
}

/** The page a session URL shows when its order cannot be reopened here. */
const PfSessionUnavailable: React.FC<{ ordered?: boolean, orderId?: number, failed?: boolean, onRetry?: () => void }> = ({ ordered = false, orderId, failed = false, onRetry }) => {
  const { t } = useTranslation(['purchase_flow_revamp'])
  const navigate = useNavigate()
  const root = usePfRootPath()
  const { isEmbed } = usePfEmbed()

  const startNewOrder = () => {
    usePfStore.getState().resetPersistedState()
    navigate(root, { replace: true })
  }

  return (
    <Stack alignItems="center" justifyContent="center" minHeight="100vh" bgcolor={BEIGE_300} px={2} data-cy="pf-revamp-session-unavailable">
      <Stack maxWidth="58rem" alignItems="center" textAlign="center" gap={3.2}>
        <Typography variant="display-sm" fontWeight={300} fontFamily={RhetorikFontFamily}>
          {t(failed
            ? 'purchase_flow_revamp:session_restore.error_title'
            : ordered ? 'purchase_flow_revamp:session_restore.ordered_title' : 'purchase_flow_revamp:session_restore.missing_title')}
        </Typography>
        <Typography variant="text-md" color={GRAY_700}>
          {failed
            ? t('purchase_flow_revamp:session_restore.error_text')
            : ordered
              ? t('purchase_flow_revamp:session_restore.ordered_text', { orderId: orderId ?? '' })
              : t('purchase_flow_revamp:session_restore.missing_text')}
        </Typography>
        <Stack direction="row" gap={1.2} flexWrap="wrap" justifyContent="center">
          {ordered && !isEmbed &&
            <MUIButton size="lg" type="secondaryBorder" onClick={() => navigate(Path.ORDERS)} data-cy="pf-revamp-session-go-to-orders">
              {t('purchase_flow_revamp:session_restore.go_to_orders')}
            </MUIButton>
          }
          {failed && onRetry &&
            <MUIButton size="lg" type="secondaryBorder" onClick={onRetry} data-cy="pf-revamp-session-retry">
              {t('purchase_flow_revamp:catalog.session_error_retry')}
            </MUIButton>
          }
          <MUIButton size="lg" type="defaultPrimary" onClick={startNewOrder} data-cy="pf-revamp-session-start-new">
            {t('purchase_flow_revamp:session_restore.start_new')}
          </MUIButton>
        </Stack>
      </Stack>
    </Stack>
  )
}

/** One line per restore outcome, so a client who reports "my order is gone" can be traced to why. */
const logRestore = (outcome: string, sessionId: string, error?: unknown, workspaceId?: string) => {
  const options = { context: { component: 'PfSessionRestore', action: 'restore', metadata: { outcome, sessionId, workspaceId } } }
  if (error === undefined) logger.info('pf-revamp session restore', undefined, options)
  else logger.warn('pf-revamp session restore failed', error, options)
}
